import type { components } from '@/api/schema'
import { toCsv } from '@/lib/csv'

type FindingGroup = components['schemas']['FindingGroupOut']

/**
 * The column as a spreadsheet: one row per finding, the evidence flattened.
 *
 * `detail` differs per code (`app/seo/findings.py`), so the columns a
 * developer most wants are lifted out by name — the status a broken target
 * answered, the pages linking to it, the links a page could add — and the
 * rest goes in one `detail` column as JSON, so nothing the audit knew is lost
 * on the way to the file.
 */
export function findingsCsv(groups: FindingGroup[], domain: string): string {
  const rows = groups.flatMap((group) =>
    group.findings.map((finding) => {
      const detail = finding.detail ?? {}
      const status = detail['status']
      const linkedFrom = detail['linked_from']
      const suggestions = detail['suggestions']
      return [
        domain,
        finding.severity,
        finding.code,
        finding.title,
        finding.url ?? '',
        typeof status === 'number' || typeof status === 'string' ? String(status) : '',
        Array.isArray(linkedFrom) ? linkedFrom.map(String).join(' ') : '',
        Array.isArray(suggestions) ? suggestions.flatMap(suggestionText).join('; ') : '',
        finding.remedy,
        JSON.stringify(detail),
      ]
    }),
  )
  return toCsv(
    [
      'site',
      'severity',
      'code',
      'finding',
      'page',
      'status',
      'linked_from',
      'suggested_links',
      'remedy',
      'detail',
    ],
    rows,
  )
}

/**
 * `link "sliding wardrobes" to https://kavya.in/wardrobes` — one cell, readable
 * unparsed. A malformed entry contributes nothing rather than an empty clause.
 */
function suggestionText(entry: unknown): string[] {
  if (typeof entry !== 'object' || entry === null) return []
  const record = entry as Record<string, unknown>
  const anchor = record['anchor']
  const url = record['url']
  if (typeof anchor !== 'string' || typeof url !== 'string') return []
  return [`link "${anchor}" to ${url}`]
}
